import { useState } from "react";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountStatementsApi, type EmailStatementRequest } from "@/lib/clients-accounts";
import { DetailPage } from "@/archetypes/DetailPage";
import { StatementPdfFrame } from "@/components/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DetailsDrawer } from "@/components/details-drawer";
import { toast } from "@/hooks/use-toast";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function AccountStatementDetailPage() {
  const { id = "0", stmtId = "0" } = useParams();
  const accountId = Number(id);
  const statementId = Number(stmtId);
  const qc = useQueryClient();
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  const { data: stmt } = useQuery({
    queryKey: ["statement", statementId],
    queryFn: () => accountStatementsApi.get(statementId),
  });

  const regenMut = useMutation({
    mutationFn: () => accountStatementsApi.regenerate(statementId),
    onSuccess: (r) => {
      toast.success(r.warning ?? "Regenerated");
      void qc.invalidateQueries({ queryKey: ["statement", statementId] });
    },
    onError: () => toast.error("Regenerate failed"),
  });
  const emailMut = useMutation({
    mutationFn: (body: EmailStatementRequest) => accountStatementsApi.email(statementId, body),
    onSuccess: () => { toast.success("Sent"); setEmailDrawerOpen(false); },
    onError: () => toast.error("Send failed"),
  });

  return (
    <DetailPage
      title={stmt ? `Statement ${stmt.statementNo}` : "Loading…"}
      backTo={`/accounts/${accountId}/statements`}
      primaryAction={
        stmt && (
          <div className="flex gap-2">
            <a href={accountStatementsApi.pdfUrl(statementId)} target="_blank" rel="noreferrer">
              <Button variant="outline">Download PDF</Button>
            </a>
            <Button variant="outline" onClick={() => { setEmailTo(stmt.recipientEmailAddress ?? ""); setEmailDrawerOpen(true); }}>
              Email
            </Button>
            <Button variant="outline" onClick={() => regenMut.mutate()} disabled={regenMut.isPending}>
              Regenerate
            </Button>
          </div>
        )
      }
      tabs={stmt ? [{
        id: "preview", label: "Preview",
        content: (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-xs text-muted-foreground">Period</dt><dd>{new Date(stmt.periodFromDate).toLocaleDateString("en-AU")} — {new Date(stmt.periodToDate).toLocaleDateString("en-AU")}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Type</dt><dd>{stmt.outstandingBalTypeStmt ? "Outstanding-per-invoice" : "Running-balance"}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Opening</dt><dd>{fmt.format(stmt.openingBalance)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Closing</dt><dd>{fmt.format(stmt.closingBalance)}</dd></div>
            </dl>
            <StatementPdfFrame src={accountStatementsApi.pdfUrl(statementId)} title={`Statement ${stmt.statementNo}`} />
          </div>
        ),
      }] : []}
      defaultTab="preview"
    >
      <DetailsDrawer open={emailDrawerOpen} onOpenChange={setEmailDrawerOpen} title="Email statement">
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); emailMut.mutate({ toEmailAddress: emailTo, ccEmailAddress: null }); }}>
          <div>
            <Label htmlFor="email-to">To</Label>
            <Input id="email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} required />
          </div>
          <Button type="submit" disabled={emailMut.isPending}>{emailMut.isPending ? "Sending…" : "Send"}</Button>
        </form>
      </DetailsDrawer>
    </DetailPage>
  );
}
